import { db, CompactSession, ScoutingSession, ScoutingPoint } from '../db';
import { generateUUID, calculateCompactPestPercentages } from '../utils';

export interface PromoteOptions {
  /** Override the session date. Defaults to cs.lastUpdated → cs.startTime → now. */
  dateOverride?: string;
  /**
   * Skip the standard pest-percentage calculation and use this map directly.
   * Used by ScoutingWizard ADD TREE mode, which combines prior + new tally before calling.
   */
  pestMetadataOverride?: Record<string, { count: number; percent: number; level?: string }>;
  /** Override totalInspectionPoints stored in session metadata. */
  totalInspectionPointsOverride?: number;
}

/**
 * Promotes a CompactSession to a ScoutingSession in the sync queue.
 *
 * Converts compact records → ScoutingPoints, calculates pest metadata,
 * creates a new ScoutingSession with syncStatus:'pending', re-links any
 * captured photos, and marks the compact session as isComplete:1.
 *
 * Safe to call from within an existing Dexie transaction — all DB writes
 * participate automatically in the outer transaction zone.
 *
 * @returns The auto-incremented id of the newly created ScoutingSession.
 */
export const promoteCompactSession = async (
  cs: CompactSession,
  options?: PromoteOptions
): Promise<number> => {
  const allPoints: ScoutingPoint[] = [];
  const tally: Record<string, number> = {};

  Object.entries(cs.records || {}).forEach(([indexStr, record]) => {
    const index = parseInt(indexStr, 10);
    if (record.pestId || (record as any).photoIds?.length) {
      const point: ScoutingPoint = {
        treeIndex: Math.floor(index / 10) + 1,
        pointIndex: (index % 10) + 1,
        pestId: record.pestId ?? '',
        timestamp: record.timestamp,
        latitude: record.lat,
        longitude: record.lon,
        photoIds: (record as any).photoIds
      };
      allPoints.push(point);
      if (record.pestId) {
        tally[record.pestId] = (tally[record.pestId] || 0) + 1;
      }
    }
  });

  const photoIds = allPoints.flatMap(p => p.photoIds || []);

  // Use caller-supplied metadata (ADD TREE mode) or compute from records
  let pestMetadata: Record<string, { count: number; percent: number; level?: string }>;
  if (options?.pestMetadataOverride) {
    pestMetadata = options.pestMetadataOverride;
  } else {
    pestMetadata = calculateCompactPestPercentages(tally, cs.records);

    // Merge orchard-level count pests
    Object.entries(cs.pestCounts || {}).forEach(([pestId, count]) => {
      if (count > 0) {
        pestMetadata[pestId] = { ...(pestMetadata[pestId] || { count: 0, percent: 0 }), count };
      }
    });

    // Merge orchard-level level pests
    Object.entries(cs.pestLevels || {}).forEach(([pestId, level]) => {
      if (level) {
        pestMetadata[pestId] = { ...(pestMetadata[pestId] || { count: 0, percent: 0 }), level };
      }
    });
  }

  const totalInspectionPoints =
    options?.totalInspectionPointsOverride ?? cs.totalInspectionPoints;

  const dateStr =
    options?.dateOverride ?? (cs.lastUpdated || cs.startTime || new Date().toISOString());

  const session: ScoutingSession = {
    uuid: generateUUID(),
    scouterName: cs.scouterName,
    orchardName: cs.orchardName,
    date: dateStr,
    points: allPoints,
    syncStatus: 'pending',
    source: 'field',
    metadata: {
      orchardId: cs.orchardId,
      isCompact: true,
      ...(totalInspectionPoints !== undefined ? { totalInspectionPoints } : {}),
      pests: pestMetadata
    }
  };

  const newSessionId = (await db.scoutingSessions.add(session)) as number;

  if (photoIds.length > 0) {
    await db.media.where('id').anyOf(photoIds).modify({ sessionId: newSessionId });
  }

  // Mark the compact session as promoted and save final state
  await db.compactSessions.update(cs.id!, {
    isComplete: 1,
    currentIndex: cs.currentIndex,
    records: cs.records as any,
    lastUpdated: new Date().toISOString()
  });

  return newSessionId;
};
